import React, { useState, useEffect } from 'react';
import communityDashboardService, { HivResultData } from '../services/communityDashboardService';
import { toast } from '../utils/toast';
import '../styles/CommunityPatientDetailModal.css';

interface CommunityPatientDetailModalProps {
  patientId: number;
  onClose: () => void;
  onPatientUpdated: () => void;
}

const CommunityPatientDetailModal: React.FC<CommunityPatientDetailModalProps> = ({
  patientId,
  onClose,
  onPatientUpdated
}) => {
  const [patient, setPatient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [hivFormData, setHivFormData] = useState<HivResultData>({
    testDate: new Date().toISOString().split('T')[0],
    result: 'NEGATIVE',
    notes: ''
  });
  const [hivResultFiles, setHivResultFiles] = useState<File[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Insurance edit state
  const [isEditingInsurance, setIsEditingInsurance] = useState(false);
  const [insuranceFormData, setInsuranceFormData] = useState({
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_group_number: ''
  });

  const handleUpdateInsurance = async () => {
    try {
      setUploadLoading(true);
      await communityDashboardService.updatePatientInsurance(patientId, insuranceFormData);
      toast.success('Insurance information updated successfully');
      setIsEditingInsurance(false);
      await loadPatientDetail();
      onPatientUpdated();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update insurance information');
    } finally {
      setUploadLoading(false);
    }
  };

  useEffect(() => {
    loadPatientDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const loadPatientDetail = async () => {
    try {
      setLoading(true);
      const data = await communityDashboardService.getPatientDetail(patientId);
      console.log('=== PATIENT DATA DEBUG ===');
      console.log('Full patient object:', data);
      console.log('Has currentCase?', !!data.currentCase);
      console.log('currentCase value:', data.currentCase);
      console.log('currentCase.status:', data.currentCase?.status);
      console.log('=========================');
      console.log('=========================');
      setPatient(data);
      // Initialize insurance form data
      setInsuranceFormData({
        insurance_provider: data.insurance_provider || '',
        insurance_policy_number: data.insurance_policy_number || '',
        insurance_group_number: data.insurance_group_number || ''
      });
    } catch (err) {
      console.error('Error loading patient:', err);
      toast.error('Failed to load patient details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleUploadHivResult = async () => {
    if (uploadLoading) return;

    try {
      setUploadLoading(true);

      // Create FormData to handle file uploads
      const formData = new FormData();
      formData.append('testDate', hivFormData.testDate);
      formData.append('result', hivFormData.result);
      if (hivFormData.notes) {
        formData.append('notes', hivFormData.notes);
      }

      // Append HIV result files
      hivResultFiles.forEach((file) => {
        formData.append('hivResultFiles', file);
      });

      const response = await communityDashboardService.uploadHivResult(patientId, formData);

      // Show appropriate message based on prerequisites
      const responseAny = response as any;
      if (responseAny.prepCase?.status === 'LABS_COMPLETED') {
        toast.success('✅ HIV test result uploaded - All prerequisites met! Status: LABS COMPLETED');
      } else if (responseAny.prepCase?.status === 'HIV_TEST_COMPLETED') {
        toast.success('✅ HIV test uploaded successfully. Waiting on: Intake Block B completion');
      } else {
        toast.success('HIV test result uploaded successfully');
      }

      await loadPatientDetail();
      onPatientUpdated();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to upload HIV result');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleUpdatePrepCaseStatus = async () => {
    if (uploadLoading || !selectedStatus) return;

    // If LABS_COMPLETED is selected, validate HIV upload form
    if (selectedStatus === 'LABS_COMPLETED') {
      if (hivResultFiles.length === 0) {
        toast.error('Please upload HIV test result document');
        return;
      }
      // Upload HIV result - status will be determined by backend based on prerequisites
      await handleUploadHivResult();
      // Reset form state after upload completes and data refreshes
      setShowStatusUpdate(false);
      setSelectedStatus('');
      setHivFormData({
        testDate: new Date().toISOString().split('T')[0],
        result: 'NEGATIVE',
        notes: ''
      });
      setHivResultFiles([]);
      return;
    }

    try {
      setUploadLoading(true);
      await communityDashboardService.updatePrepCaseStatus(patientId, selectedStatus);
      toast.success(`Patient status updated to ${selectedStatus.replace(/_/g, ' ')}`);
      // Reset form state
      setShowStatusUpdate(false);
      setSelectedStatus('');
      await loadPatientDetail();
      onPatientUpdated();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update patient status');
    } finally {
      setUploadLoading(false);
    }
  };
  const handleHivResultFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setHivResultFiles(Array.from(e.target.files));
    }
  };


  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content community-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Loading patient details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) return null;

  const patientInitials = `${patient.firstName?.[0] || 'P'}${patient.lastName?.[0] || 'A'}`;
  const fullName = `${patient.firstName} ${patient.lastName}`;

  // Helper to format date for display
  const formatDateForDisplay = (dateString: string | null) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content community-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>
            <span className="modal-icon">👤</span>
            Patient Detail View
          </h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Top Info Card */}
          <div className="patient-info-card">
            <div className="patient-info-body">
              <div className="patient-profile-section">
                <div className="patient-avatar-large">
                  {patientInitials}
                </div>
                <div className="patient-details-content">
                  <div className="patient-id-badge">#PT{String(patient.id).padStart(4, '0')}</div>
                  <h1 className="patient-name-title">{fullName}</h1>
                  <p className="patient-address-text">
                    {patient.address_line_1 || '4150 Hiney Road'}
                    {patient.address_line_2 && `, ${patient.address_line_2}`}
                    <br />
                    {patient.city || 'Las Vegas'}, {patient.state || 'NV'} {patient.zip || '89109'}
                  </p>

                  <div className="patient-contact-info">
                    <div className="contact-item">
                      <span className="contact-icon">📞</span>
                      Phone: <span style={{ color: '#0f172a', fontWeight: '600' }}>{patient.phoneNumber || '+1 54546 45648'}</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">📧</span>
                      Email: <span style={{ color: '#0f172a', fontWeight: '600' }}>{patient.email}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="patient-status-section">
                <div className="status-group">
                  <span className="status-text-item">Market:</span>
                  <span className={`status-text-item chip ${patient.market === "OUT_OF_MARKET" ? 'pending' : 'verified'}`}>
                    {patient.market.replace(/_/g, ' ').toLowerCase()}
                  </span>
                </div>

                <div className="status-group">
                  <span className="status-text-item">Status:</span>
                  <span className={`status-text-item chip ${patient.display_status === 'Active' ? 'verified' : 'pending'}`}>
                    {patient.display_status.toLowerCase()}
                  </span>
                </div>

                <div className="status-group">
                  <span className="status-text-item">Insurance:</span>
                  <span className={`status-text-item chip ${patient.insurance_verified ? 'verified' : 'pending'}`}>
                    {patient.insurance_verified ? 'Verified' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 4 Boxes Grid */}
          <div className="dashboard-grid-v2">

            {/* Medications */}
            <div className="feature-card">
              <div className="feature-card-header">
                <h3 className="feature-card-title">
                  <div className="feature-icon-box icon-meds">💊</div>
                  Medications
                </h3>
              </div>
              <div className="feature-card-content">
                <div className="medication-details">
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Medication Name</span>
                      <span className="item-value">{patient.medication_name || 'Truvada'}</span>
                    </div>
                  </div>
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">PrEP Type</span>
                      <span className="item-value">{patient.prep_type || 'Daily Oral'}</span>
                    </div>
                  </div>
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Status</span>
                      <span className={`status-text-item chip ${patient.prescription_issued ? 'verified' : 'pending'}`} style={{ minWidth: '80px' }}>
                        {patient.prescription_issued ? 'Issued' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notifications & Status Update */}
            <div className="feature-card">
              <div className="feature-card-header">
                <h3 className="feature-card-title">
                  <div className="feature-icon-box icon-notifs">🔔</div>
                  Patient Progress
                </h3>
                {patient.currentCase && patient.currentCase.status !== 'LABS_COMPLETED' && (
                  <button
                    className="milestone-action-btn"
                    onClick={() => setShowStatusUpdate(!showStatusUpdate)}
                  >
                    {showStatusUpdate ? 'Cancel' : 'Update Status'}
                  </button>
                )}
              </div>
              <div className="feature-card-content">
                {!showStatusUpdate ? (
                  <div className={`notification-banner-style ${patient.display_status === 'Active' ? 'status-active' : 'status-pending'}`} style={{ padding: '16px', borderRadius: '12px', background: patient.display_status === 'Active' ? '#f0fdf4' : '#fffbeb', border: '1px solid currentColor', color: patient.display_status === 'Active' ? '#166534' : '#854d0e' }}>
                    <div className="notif-content">
                      <div className="notif-title" style={{ fontWeight: 700, marginBottom: '4px' }}>Current Case Status</div>
                      <div className="notif-message" style={{ fontSize: '13px' }}>
                        {patient.currentCase?.status?.replace(/_/g, ' ') || 'No active case'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="status-update-form" style={{ border: 'none', padding: 0 }}>
                    <div className="form-group">
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="form-select"
                      >
                        <option value="">-- Select Status --</option>
                        <option value="HIV_SAMPLE_COLLECTED">HIV Sample Collected</option>
                        <option value="HIV_TEST_COMPLETED">HIV Test Completed</option>
                        <option value="LABS_SCHEDULING">Labs Scheduling</option>
                        <option value="LABS_SCHEDULED">Labs Scheduled</option>
                        <option value="AWAITING_LABS">Awaiting Labs</option>
                        <option value="LABS_COMPLETED">Labs Completed (Upload HIV Result)</option>
                      </select>
                    </div>
                    {selectedStatus !== 'LABS_COMPLETED' && (
                      <button
                        className="submit-btn"
                        onClick={handleUpdatePrepCaseStatus}
                        disabled={uploadLoading || !selectedStatus}
                      >
                        {uploadLoading ? '⏳ Updating...' : '✓ Update Status'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Refills & Adherence */}
            <div className="feature-card">
              <div className="feature-card-header">
                <h3 className="feature-card-title">
                  <div className="feature-icon-box icon-refills">🔄</div>
                  Refills & Adherence
                </h3>
              </div>
              <div className="feature-card-content">
                <div className="medication-details">
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Last Prescription Filled</span>
                      <span className="item-value">{formatDateForDisplay(patient.updatedAt)}</span>
                    </div>
                  </div>
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Next Refill Date</span>
                      <span className="item-value">TBD</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Appointments */}
            <div className="feature-card">
              <div className="feature-card-header">
                <h3 className="feature-card-title">
                  <div className="feature-icon-box icon-appts">📅</div>
                  Timeline
                </h3>
              </div>
              <div className="feature-card-content">
                <div className="medication-details">
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">HIV Lab Test</span>
                      <span className="item-value">{formatDateForDisplay(patient.hiv_test_date)}</span>
                    </div>
                    <span className={`status-text-item chip ${patient.hiv_test_completed ? 'verified' : 'pending'}`} style={{ minWidth: '80px' }}>
                      {patient.hiv_test_completed ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Telehealth Visit</span>
                      <span className="item-value">{patient.telehealth_completed ? 'Completed' : 'TBD'}</span>
                    </div>
                    <span className={`status-text-item chip ${patient.telehealth_completed ? 'verified' : 'pending'}`} style={{ minWidth: '80px' }}>
                      {patient.telehealth_completed ? 'Done' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* HIV Test Upload Section (Full Width if Status Update is Hive) */}
          {selectedStatus === 'LABS_COMPLETED' && (
            <div className="feature-card" style={{ marginTop: '24px' }}>
              <div className="feature-card-header">
                <h3 className="feature-card-title">📂 Upload HIV Test Results</h3>
              </div>
              <div className="feature-card-content">
                <div className="status-update-form" style={{ border: 'none', padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label>Test Date:</label>
                    <input
                      type="date"
                      value={hivFormData.testDate}
                      onChange={(e) => setHivFormData({ ...hivFormData, testDate: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Result:</label>
                    <select
                      value={hivFormData.result}
                      onChange={(e) => setHivFormData({ ...hivFormData, result: e.target.value as any })}
                      className="form-select"
                    >
                      <option value="NEGATIVE">Negative (Eligible for PrEP)</option>
                      <option value="POSITIVE">Positive</option>
                      <option value="INCONCLUSIVE">Inconclusive</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label>HIV Test Result Document: <span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleHivResultFileSelect}
                      className="form-file-input"
                    />
                    {hivResultFiles.length > 0 && (
                      <div style={{ marginTop: '8px', padding: '8px', background: '#f8fafc', borderRadius: '4px', fontSize: '13px' }}>
                        Selected: {hivResultFiles.map(f => f.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label>Notes (Optional):</label>
                    <textarea
                      value={hivFormData.notes}
                      onChange={(e) => setHivFormData({ ...hivFormData, notes: e.target.value })}
                      className="form-textarea"
                      rows={2}
                      placeholder="Additional notes about the test..."
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <button
                      className="submit-btn"
                      onClick={handleUpdatePrepCaseStatus}
                      disabled={uploadLoading}
                    >
                      {uploadLoading ? '⏳ Uploading...' : '✓ Confirm Results & Update Status'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Insurance Information Section */}
          <div className="feature-card" style={{ marginTop: '24px' }}>
            <div className="feature-card-header">
              <h3 className="feature-card-title">
                <div className="feature-icon-box" style={{ background: '#dbeafe', color: '#2563eb' }}>🛡️</div>
                Insurance Information
              </h3>
              {!isEditingInsurance ? (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="btn-sm btn-outline-primary"
                    onClick={() => setIsEditingInsurance(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    Edit
                  </button>
                  {/* Keep View Insurance Card button if needed, or remove if redundant */}
                  <button
                    className="btn-sm btn-outline-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    View Insurance Card
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="btn-sm btn-secondary"
                    onClick={() => {
                      setIsEditingInsurance(false);
                      setInsuranceFormData({
                        insurance_provider: patient.insurance_provider || '',
                        insurance_policy_number: patient.insurance_policy_number || '',
                        insurance_group_number: patient.insurance_group_number || ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-sm btn-primary"
                    onClick={handleUpdateInsurance}
                    disabled={uploadLoading}
                    style={{ marginLeft: '10px' }}
                  >
                    {uploadLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            <div className="feature-card-content">
              {!isEditingInsurance ? (
                <div className="medication-details">
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Insurance Provider</span>
                      <span className="item-value">{patient.insurance_provider || 'Not provided'}</span>
                    </div>
                  </div>
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Policy Number</span>
                      <span className="item-value">{patient.insurance_policy_number || 'Not provided'}</span>
                    </div>
                  </div>
                  <div className="detailed-list-item">
                    <div className="item-left">
                      <span className="item-label">Group Number</span>
                      <span className="item-value">{patient.insurance_group_number || 'Not provided'}</span>
                    </div>
                  </div>
                  {(patient.insurance_card_front_url || patient.insurance_card_back_url) && (
                    <div style={{ marginTop: '16px' }}>
                      <p className="item-label" style={{ marginBottom: '8px' }}>Insurance Card Images</p>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {patient.insurance_card_front_url && (
                          <a href={patient.insurance_card_front_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img
                              src={patient.insurance_card_front_url}
                              alt="Insurance Card Front"
                              style={{ width: '100px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                            />
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Front</div>
                          </a>
                        )}
                        {patient.insurance_card_back_url && (
                          <a href={patient.insurance_card_back_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img
                              src={patient.insurance_card_back_url}
                              alt="Insurance Card Back"
                              style={{ width: '100px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                            />
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Back</div>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="insurance-edit-form">
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#64748b' }}>Insurance Provider</label>
                    <input
                      type="text"
                      className="form-input"
                      value={insuranceFormData.insurance_provider}
                      onChange={(e) => setInsuranceFormData({ ...insuranceFormData, insurance_provider: e.target.value })}
                      placeholder="e.g. Blue Cross Blue Shield"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#64748b' }}>Policy Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={insuranceFormData.insurance_policy_number}
                      onChange={(e) => setInsuranceFormData({ ...insuranceFormData, insurance_policy_number: e.target.value })}
                      placeholder="Policy ID"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#64748b' }}>Group Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={insuranceFormData.insurance_group_number}
                      onChange={(e) => setInsuranceFormData({ ...insuranceFormData, insurance_group_number: e.target.value })}
                      placeholder="Group ID"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PrEP Journey Workflow */}
          <div className="journey-section">
            <h3 className="journey-title">PrEP Journey Workflow</h3>
            <div className="journey-steps-container">
              <div className={`journey-step-item ${patient.intake_block_b_completed ? 'completed' : ''}`}>
                <span className="step-icon">{patient.intake_block_b_completed ? '✅' : '⏳'}</span>
                Intake Block B
              </div>
              <div className={`journey-step-item ${patient.hiv_test_completed ? 'completed' : ''}`}>
                <span className="step-icon">{patient.hiv_test_completed ? '✅' : '⏳'}</span>
                HIV Test
              </div>
              <div className={`journey-step-item ${patient.insurance_verified ? 'completed' : ''}`}>
                <span className="step-icon">{patient.insurance_verified ? '✅' : '⏳'}</span>
                Insurance Verified
              </div>
              <div className={`journey-step-item ${patient.telehealth_completed ? 'completed' : ''}`}>
                <span className="step-icon">{patient.telehealth_completed ? '✅' : '⏳'}</span>
                Telehealth Visit
              </div>
              <div className={`journey-step-item ${patient.prescription_issued ? 'completed' : ''}`}>
                <span className="step-icon">{patient.prescription_issued ? '✅' : '⏳'}</span>
                Prescription Issued
              </div>
            </div>
          </div>

          <div style={{ marginTop: '24px', padding: '16px', borderRadius: '12px', background: '#eff6ff', color: '#1e40af', fontSize: '14px', border: '1px solid #bfdbfe' }}>
            <span style={{ marginRight: '8px' }}>ℹ️</span>
            As a community partner, you can track referral progress and upload HIV test results. Insurance verification and medication fulfillment are managed by the pharmacy partner.
          </div>

        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close Detail View</button>
        </div>
      </div>
    </div>
  );
};

export default CommunityPatientDetailModal;
